/** @module @liquidefy/code-editor/adapters/adapter-tools @version 2.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  LanguageId,
  DiagnosticInfo,
  ExternalToolConfig,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asLanguageId,
  now,
} from '../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 LOCAL TYPES — ExternalTool, ToolOutput, ToolAdapterState
// ═══════════════════════════════════════════════════════════════════════════════

/** A registered external tool with availability and version info. */
export interface ExternalTool {
  readonly name: string;
  readonly command: string;
  readonly args: string[];
  readonly languages: LanguageId[];
  readonly formatOnSave: boolean;
  readonly lintOnSave: boolean;
  readonly available: boolean;
  readonly version?: string;
  readonly config: Record<string, unknown>;
}

/** Result of running an external tool command. */
export interface ToolOutput {
  readonly tool: string;
  readonly exitCode: number;
  readonly stdout: string;
  readonly stderr: string;
  readonly durationMs: number;
}

/** Configuration passed to createToolAdapterState. */
export interface ToolAdapterConfig {
  readonly toolPaths?: Record<string, string>;
  readonly defaultTimeout?: number;
}

/** State container for the tool adapter module. */
export interface ToolAdapterState {
  readonly tools: Map<string, ExternalTool>;
  readonly config: ToolAdapterConfig;
  readonly createdAt: number;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 FACTORY — createToolAdapterState
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Creates a new ToolAdapterState with an empty tool registry.
 * @param config - Optional configuration for tool paths and timeouts.
 * @returns Fresh ToolAdapterState.
 */
export function createToolAdapterState(config: ToolAdapterConfig = {}): ToolAdapterState {
  return {
    tools: new Map(),
    config,
    createdAt: now(),
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 REGISTRATION — registerTool, configureToolDefaults
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Registers an external tool in the adapter state.
 * @param state - Current adapter state.
 * @param tool - Tool to register.
 * @returns EditorResult wrapping updated ToolAdapterState.
 */
export function registerTool(
  state: ToolAdapterState,
  tool: ExternalTool
): EditorResult<ToolAdapterState> {
  if (!tool.name) {
    return err('INVALID_TOOL', 'Tool must have a non-empty "name"');
  }
  if (!tool.command) {
    return err('INVALID_TOOL', `Tool "${tool.name}" must have a "command"`);
  }
  if (state.tools.has(tool.name)) {
    return err('TOOL_EXISTS', `Tool "${tool.name}" is already registered`);
  }

  const updated = new Map(state.tools);
  updated.set(tool.name, tool);

  return ok(
    { ...state, tools: updated },
    'TOOL_REGISTERED',
    `Tool "${tool.name}" registered for languages: ${tool.languages.join(', ') || 'all'}`
  );
}

/**
 * Updates the default configuration for a registered tool.
 * @param state - Current adapter state.
 * @param toolName - Tool to configure.
 * @param config - Configuration key-value pairs to merge.
 * @returns EditorResult wrapping updated ToolAdapterState.
 */
export function configureToolDefaults(
  state: ToolAdapterState,
  toolName: string,
  config: Record<string, unknown>
): EditorResult<ToolAdapterState> {
  const tool = state.tools.get(toolName);
  if (!tool) {
    return err('TOOL_NOT_FOUND', `Tool "${toolName}" is not registered`);
  }

  const updated = new Map(state.tools);
  updated.set(toolName, { ...tool, config: { ...tool.config, ...config } });

  return ok(
    { ...state, tools: updated },
    'TOOL_CONFIGURED',
    `Tool "${toolName}" configuration updated`
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 AVAILABILITY — isToolAvailable, getToolVersion
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Returns whether a named tool is registered and marked as available.
 * @param state - Current adapter state.
 * @param toolName - Tool name to check.
 * @returns True if the tool is registered and available.
 */
export function isToolAvailable(state: ToolAdapterState, toolName: string): boolean {
  const tool = state.tools.get(toolName);
  return tool?.available === true;
}

/**
 * Returns the version string for a registered tool.
 * @param state - Current adapter state.
 * @param toolName - Tool name to look up.
 * @returns EditorResult wrapping the version string.
 */
export function getToolVersion(
  state: ToolAdapterState,
  toolName: string
): EditorResult<string> {
  const tool = state.tools.get(toolName);
  if (!tool) return err('TOOL_NOT_FOUND', `Tool "${toolName}" is not registered`);
  if (!tool.available) return err('TOOL_UNAVAILABLE', `Tool "${toolName}" is not available`);
  if (!tool.version) return err('VERSION_UNKNOWN', `Version for "${toolName}" is not known`);
  return ok(tool.version, 'VERSION_RETRIEVED', `Tool "${toolName}" version: ${tool.version}`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 LANGUAGE SELECTION — getToolForLanguage
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Finds the best available tool for a given language ID.
 * Priority: tools with formatOnSave=true first, then general availability.
 * @param state - Current adapter state.
 * @param languageId - Language to find a tool for.
 * @returns The best ExternalTool or null if none are available.
 */
export function getToolForLanguage(
  state: ToolAdapterState,
  languageId: LanguageId
): ExternalTool | null {
  const candidates = Array.from(state.tools.values()).filter(
    (t) => t.available && (t.languages.length === 0 || t.languages.includes(languageId))
  );

  if (candidates.length === 0) return null;
  return candidates.sort((a, b) => (b.formatOnSave ? 1 : 0) - (a.formatOnSave ? 1 : 0))[0]!;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §7 TOOL OPERATIONS — formatDocument, lintDocument, typecheckDocument, runToolCommand
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Formats document content using the best available formatter for the language.
 * In production, this delegates to the IO layer which shells out to the tool.
 * Here we return the content unchanged with a note that the IO boundary must handle execution.
 * @param state - Current adapter state.
 * @param content - Document content to format.
 * @param languageId - Language of the document.
 * @param options - Optional format options (tab size, print width, etc.).
 * @returns EditorResult wrapping formatted content string.
 */
export function formatDocument(
  state: ToolAdapterState,
  content: string,
  languageId: LanguageId,
  options?: Record<string, unknown>
): EditorResult<string> {
  const tool = getToolForLanguage(state, languageId);
  if (!tool) {
    return err(
      'NO_FORMATTER',
      `No available formatter for language "${languageId}". ` +
      `Available tools: ${Array.from(state.tools.keys()).join(', ') || 'none'}`
    );
  }

  // In a real implementation, the IO layer would exec the tool here.
  // This engine layer returns the content as-is and the event signals the IO boundary.
  return ok(
    content,
    'FORMAT_QUEUED',
    `Formatting with "${tool.name}" — IO layer must exec: ${tool.command} ${tool.args.join(' ')}`
  );
}

/**
 * Lints document content using available linters for the language.
 * Returns an empty diagnostic list — the IO layer populates diagnostics from tool output.
 * @param state - Current adapter state.
 * @param content - Document content to lint.
 * @param languageId - Language of the document.
 * @returns EditorResult wrapping DiagnosticInfo[].
 */
export function lintDocument(
  state: ToolAdapterState,
  content: string,
  languageId: LanguageId
): EditorResult<DiagnosticInfo[]> {
  const tool = getToolForLanguage(state, languageId);
  if (!tool) {
    return ok(
      [],
      'NO_LINTER',
      `No linter available for language "${languageId}"`
    );
  }

  // IO layer must exec the linter and parse output into DiagnosticInfo[].
  return ok(
    [],
    'LINT_QUEUED',
    `Linting with "${tool.name}" — IO layer must exec and parse output`
  );
}

/**
 * Type-checks a document via the language's type checker.
 * Returns empty diagnostics — real output must come from the IO layer.
 * @param state - Current adapter state.
 * @param filePath - Absolute path to the file to type-check.
 * @returns EditorResult wrapping DiagnosticInfo[].
 */
export function typecheckDocument(
  state: ToolAdapterState,
  filePath: string
): EditorResult<DiagnosticInfo[]> {
  // Find any typecheck-capable tool (tsc, pyright, rust-analyzer, etc.)
  const typecheckers = ['typescript', 'tsc', 'pyright', 'rust-analyzer', 'gopls', 'mypy'];
  const tool = typecheckers
    .map((name) => state.tools.get(name))
    .find((t) => t?.available);

  if (!tool) {
    return ok(
      [],
      'NO_TYPECHECKER',
      `No type checker available. Register one of: ${typecheckers.join(', ')}`
    );
  }

  return ok(
    [],
    'TYPECHECK_QUEUED',
    `Type-checking "${filePath}" with "${tool.name}" — IO layer must exec and parse output`
  );
}

/**
 * Runs a raw tool command with provided arguments.
 * @param state - Current adapter state.
 * @param tool - Tool to run.
 * @param args - Arguments to pass to the tool.
 * @returns EditorResult wrapping a ToolOutput envelope.
 */
export function runToolCommand(
  state: ToolAdapterState,
  tool: ExternalTool,
  args: string[]
): EditorResult<ToolOutput> {
  if (!tool.available) {
    return err('TOOL_UNAVAILABLE', `Tool "${tool.name}" is not available`);
  }

  // Return a placeholder output — real execution happens in IO.
  const output: ToolOutput = {
    tool: tool.name,
    exitCode: 0,
    stdout: '',
    stderr: '',
    durationMs: 0,
  };

  return ok(
    output,
    'TOOL_COMMAND_QUEUED',
    `IO layer must exec: ${tool.command} ${[...tool.args, ...args].join(' ')}`
  );
}
